import { useState } from "react";

import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationProgressBar } from "./PersonalisationProgressBar";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";
import { SingleChoiceTagPicker } from "./SingleChoiceTagPicker";

interface SkinBalanceItem {
  id: string;
  label: string;
}

interface SkinBalanceProps {
  options?: SkinBalanceItem[];
  onContinue: (selection: string) => void;
  onSkip?: () => void;
  selectedColor?: string;
  continueButtonText?: string;
  skipText?: string;
  progressPercentage: number;
  onBack?: () => void;
  onClose?: () => void;
  initialSelectedId?: string | null;
}

const defaultSkinBalanceOptions: SkinBalanceItem[] = [
  { id: "very-dry", label: "Very dry — craves hydration" },
  { id: "mostly-dry", label: "Mostly dry — needs moisture balance" },
  { id: "combination", label: "Combination — varies by area" },
  { id: "mostly-oily", label: "Mostly oily — produces natural shine" },
  { id: "balanced", label: "Balanced — naturally even" },
];

export const SkinBalance = ({
  options = defaultSkinBalanceOptions,
  onContinue,
  onSkip,
  selectedColor,
  continueButtonText = "Continue",
  skipText = "Skip for now",
  progressPercentage,
  onBack,
  onClose,
  initialSelectedId,
}: SkinBalanceProps) => {
  const [selected, setSelected] = useState<SkinBalanceItem | null>(
    initialSelectedId
      ? options.find((o) => o.id === initialSelectedId) ?? null
      : null
  );

  const handleSelectionChange = (item: SkinBalanceItem | null) => {
    setSelected(item);
    onContinue?.(item?.id ?? "");
  };

  const handleContinue = () => {
    if (!selected) {
      return;
    }
    onContinue(selected.id);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  const isContinueDisabled = selected === null;

  return (
    <PersonalisationScreenLayout
      header={
        <PersonalisationProgressBar
          progressPercentage={progressPercentage}
          onBack={handleBack}
          onClose={handleClose}
        />
      }
      footer={
        <>
          <Button
            style="primary"
            size="large"
            fullWidth
            disabled={isContinueDisabled}
            onClick={handleContinue}
          >
            {continueButtonText}
          </Button>

          <TextButton variant="secondary" onClick={handleSkip}>
            {skipText}
          </TextButton>
        </>
      }
    >
      <PersonalisationHeading size="sm">
        Which of these feels most like your skin?
      </PersonalisationHeading>

      <SingleChoiceTagPicker
        items={options}
        selectedId={selected?.id ?? null}
        onSelectionChangeRequest={handleSelectionChange}
        selectedColor={selectedColor}
        ariaLabel="Select your skin balance"
      />
    </PersonalisationScreenLayout>
  );
};
